import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Users, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 5:30 PM → 1:30 AM in 30-min intervals
const buildSlots = () => {
  const slots = [];
  for (let m = 17 * 60 + 30; m <= 25 * 60 + 30; m += 30) {
    const h24 = Math.floor(m / 60) % 24;
    const min = m % 60;
    const period = h24 >= 12 ? "PM" : "AM";
    let h12 = h24 % 12;
    if (h12 === 0) h12 = 12;
    slots.push(`${h12}:${min.toString().padStart(2, "0")} ${period}`);
  }
  return slots;
};
const SLOTS = buildSlots();

const todayStr = () => {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

const emptyErrors = {};

export const Reservation = () => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", date: todayStr(), time: "", partySize: "2", requests: "",
  });
  const [errors, setErrors] = useState(emptyErrors);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 7) e.phone = "Enter a valid phone number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.date) e.date = "Choose a date";
    if (!form.time) e.time = "Select a time";
    if (!form.partySize) e.partySize = "Select party size";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/reservations`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        date: form.date,
        time: form.time,
        party_size: parseInt(form.partySize, 10),
        special_requests: form.requests,
      });
      setDone(true);
    } catch (err) {
      setErrors({ submit: "Something went wrong. Please call (202) 215-5658." });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({ name: "", phone: "", email: "", date: todayStr(), time: "", partySize: "2", requests: "" });
    setErrors(emptyErrors);
    setDone(false);
  };

  return (
    <section id="reserve" className="relative py-28 lg:py-40 bg-[var(--br-panel)] border-y border-[var(--br-gold)]/12" data-testid="reservation">
      <div className="mx-auto max-w-3xl px-6">
        <Reveal>
          <div className="text-center">
            <p className="overline mb-5">Reservations</p>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl text-[var(--br-cream)] leading-[1.05]">
              Reserve your <span className="italic text-[var(--br-gold)]">table</span>
            </h2>
            <p className="mt-4 text-sm text-[var(--br-muted)]">Open nightly · 5:30 PM – 2 AM · 5815 Seminary Rd, Falls Church</p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="glass mt-12 rounded-3xl p-6 sm:p-10">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="confirm"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="py-10 text-center"
                  data-testid="reservation-confirmation"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
                    className="mx-auto mb-6 grid h-16 w-16 place-items-center rounded-full bg-[var(--br-gold)]/15 text-[var(--br-gold)]"
                  >
                    <CheckCircle2 size={34} />
                  </motion.div>
                  <h3 className="font-display text-3xl text-[var(--br-cream)]">Thank you!</h3>
                  <p className="mx-auto mt-3 max-w-md text-[var(--br-muted)] leading-relaxed">
                    Your reservation request has been received. We'll contact you if any changes are needed.
                  </p>
                  <button
                    data-testid="reservation-new-btn"
                    onClick={resetForm}
                    className="mt-8 rounded-full border border-[var(--br-gold)]/40 px-7 py-3 text-sm tracking-wide text-[var(--br-cream)] hover:border-[var(--br-gold)] transition-colors"
                  >
                    Make another reservation
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  noValidate
                  data-testid="reservation-form"
                >
                  {/* Date + Party size */}
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="overline block mb-3 text-[10px]">Date</label>
                      <input
                        type="date"
                        data-testid="res-date"
                        min={todayStr()}
                        value={form.date}
                        onChange={(e) => set("date", e.target.value)}
                        className={`field w-full rounded-xl px-4 py-3.5 text-sm ${errors.date ? "error" : ""}`}
                      />
                      {errors.date && <p className="mt-1.5 text-xs text-[#d9744f]">{errors.date}</p>}
                    </div>
                    <div>
                      <label className="overline block mb-3 text-[10px]">Party Size</label>
                      <div className="relative">
                        <Users size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--br-gold)]" />
                        <select
                          data-testid="res-party-size"
                          value={form.partySize}
                          onChange={(e) => set("partySize", e.target.value)}
                          className={`field w-full appearance-none rounded-xl pl-11 pr-4 py-3.5 text-sm ${errors.partySize ? "error" : ""}`}
                        >
                          {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                            <option key={n} value={n}>{n} {n === 1 ? "guest" : "guests"}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Time slots */}
                  <div className="mt-6">
                    <label className="overline block mb-3 text-[10px]">Select a Time</label>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5" data-testid="res-time-slots">
                      {SLOTS.map((s) => (
                        <button
                          type="button"
                          key={s}
                          data-testid={`res-slot-${s.replace(/[:\s]/g, "")}`}
                          data-active={form.time === s}
                          onClick={() => set("time", s)}
                          className="slot rounded-xl py-2.5 text-xs font-medium"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                    {errors.time && <p className="mt-2 text-xs text-[#d9744f]">{errors.time}</p>}
                  </div>

                  {/* Name / Phone */}
                  <div className="mt-6 grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="overline block mb-3 text-[10px]">Full Name</label>
                      <input
                        data-testid="res-name"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder="Your name"
                        className={`field w-full rounded-xl px-4 py-3.5 text-sm ${errors.name ? "error" : ""}`}
                      />
                      {errors.name && <p className="mt-1.5 text-xs text-[#d9744f]">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="overline block mb-3 text-[10px]">Phone</label>
                      <input
                        data-testid="res-phone"
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        placeholder="(202) 215-5658"
                        className={`field w-full rounded-xl px-4 py-3.5 text-sm ${errors.phone ? "error" : ""}`}
                      />
                      {errors.phone && <p className="mt-1.5 text-xs text-[#d9744f]">{errors.phone}</p>}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="mt-6">
                    <label className="overline block mb-3 text-[10px]">Email</label>
                    <input
                      data-testid="res-email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      placeholder="you@email.com"
                      className={`field w-full rounded-xl px-4 py-3.5 text-sm ${errors.email ? "error" : ""}`}
                    />
                    {errors.email && <p className="mt-1.5 text-xs text-[#d9744f]">{errors.email}</p>}
                  </div>

                  {/* Special requests */}
                  <div className="mt-6">
                    <label className="overline block mb-3 text-[10px]">Special Requests <span className="text-[var(--br-muted)] normal-case tracking-normal">(optional)</span></label>
                    <textarea
                      data-testid="res-requests"
                      value={form.requests}
                      onChange={(e) => set("requests", e.target.value)}
                      rows={3}
                      placeholder="Birthday, hookah, VIP booth, seating preferences…"
                      className="field w-full rounded-xl px-4 py-3.5 text-sm resize-none"
                    />
                  </div>

                  {errors.submit && <p className="mt-4 text-sm text-[#d9744f] text-center">{errors.submit}</p>}

                  <button
                    type="submit"
                    data-testid="res-submit-btn"
                    disabled={submitting}
                    className="gold-btn mt-8 w-full rounded-full py-4 text-sm font-semibold tracking-wide flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {submitting ? (<><Loader2 size={18} className="animate-spin" /> Sending…</>) : "Request Reservation"}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
